// Right-side detail pane: full mail view + actions.

import { useEffect, useMemo, useState } from "react";
import { api } from "../../shared/api/client";
import { toast } from "../../shared/stores/toasts";
import { I } from "../../shared/Icon";
import { LinkifiedText } from "../../shared/linkify";
import type { MailAttachment, MailDetail, MailLabel, MailReceipt } from "./types";
import { fmtFullDate, formatBytes } from "./types";

const EMAIL_RE = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/g;

/** "홍길동 <a@b.c>" 처럼 이름이 붙은 주소를 이름과 메일로 나눈다. */
export function splitAddr(raw: string): { name: string; email: string } {
  const t = (raw || "").trim();
  const m = t.match(/<([^>]+)>/);
  const email = (m ? m[1] : t).trim();
  const name = (m ? t.slice(0, m.index).replace(/["']/g, "").trim() : "") || email.split("@")[0];
  return { name, email };
}

/** 클립보드에 글자를 넣는다.
 *
 *  navigator.clipboard 는 보안 컨텍스트(https·localhost)에서만 동작하고 권한도 탄다.
 *  회사 안에서 http 로 열어 보는 경우가 있어, 막히면 예전 방식(textarea + execCommand)으로
 *  한 번 더 시도한다. */
async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch { /* 아래 방식으로 다시 시도 */ }
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

/**
 * 본문 텍스트 안의 메일 주소를 mailto 링크로 바꾼다.
 *
 * 텍스트 노드만 훑는다 — innerHTML 을 문자열로 치환하면 태그 속성(href, src) 안의
 * 주소까지 망가뜨린다. 이미 링크(<a>) 안이거나 style/script 안이면 건드리지 않는다.
 */
function linkifyEmails(doc: Document) {
  const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT);
  const targets: Text[] = [];
  while (walker.nextNode()) {
    const node = walker.currentNode as Text;
    if (!node.nodeValue || !EMAIL_RE.test(node.nodeValue)) continue;
    EMAIL_RE.lastIndex = 0;
    const parent = node.parentElement;
    if (!parent) continue;
    if (parent.closest("a, style, script, code")) continue;   // 링크 안·코드 안은 그대로
    targets.push(node);
  }

  for (const node of targets) {
    const text = node.nodeValue || "";
    const frag = doc.createDocumentFragment();
    let last = 0;
    text.replace(EMAIL_RE, (addr, idx: number) => {
      if (idx > last) frag.appendChild(doc.createTextNode(text.slice(last, idx)));
      const a = doc.createElement("a");
      a.setAttribute("href", `mailto:${addr}`);
      a.setAttribute("data-mail-to", addr);   // 앱 안에서 가로채 작성창을 연다
      a.textContent = addr;
      frag.appendChild(a);
      last = idx + addr.length;
      return addr;
    });
    if (last < text.length) frag.appendChild(doc.createTextNode(text.slice(last)));
    node.parentNode?.replaceChild(frag, node);
  }
}

/** 평문 본문. 줄바꿈을 살리면서 메일 주소만 눌러 쓸 수 있게 한다. */
function PlainBodyWithLinks({ text, onComposeTo }: {
  text: string; onComposeTo: (addr: string) => void;
}) {
  const parts: (string | { addr: string })[] = [];
  let last = 0;
  text.replace(EMAIL_RE, (addr, idx: number) => {
    if (idx > last) parts.push(text.slice(last, idx));
    parts.push({ addr });
    last = idx + addr.length;
    return addr;
  });
  if (last < text.length) parts.push(text.slice(last));

  return (
    <span style={{ whiteSpace: "pre-wrap" }}>
      {parts.map((p, i) =>
        typeof p === "string" ? (
          // 메일 주소 사이의 글에 웹 주소가 있으면 새 탭 링크로(예전엔 메일 주소만 눌렸다)
          <span key={i}><LinkifiedText text={p}/></span>
        ) : (
          <a key={i} href={`mailto:${p.addr}`}
             onClick={(e) => { e.preventDefault(); onComposeTo(p.addr); }}
             style={{ color: "var(--p-600)", textDecoration: "underline", cursor: "pointer" }}>
            {p.addr}
          </a>
        ),
      )}
    </span>
  );
}

/** 헤더의 주소 목록. 각 주소를 눌러 그 사람에게 새 메일을 쓴다. */
function AddrList({ list, onComposeTo, onMenu }: {
  list: string[]; onComposeTo: (a: string) => void;
  /** 주소에서 오른쪽 단추를 눌렀을 때(복사·답장·주소록 등 메뉴) */
  onMenu?: (addr: string, e: React.MouseEvent) => void;
}) {
  if (!list || list.length === 0) return <span>-</span>;
  return (
    <>
      {list.map((addr, i) => (
        <span key={addr + i}>
          {i > 0 && ", "}
          <a href={`mailto:${addr}`}
             onClick={(e) => { e.preventDefault(); onComposeTo(addr); }}
             onContextMenu={(e) => { if (onMenu) { e.preventDefault(); onMenu(addr, e); } }}
             title="클릭: 메일 쓰기 / 오른쪽 클릭: 복사·주소록 등"
             style={{ color: "var(--p-600)", textDecoration: "none", cursor: "pointer" }}
             onMouseEnter={(e) => (e.currentTarget.style.textDecoration = "underline")}
             onMouseLeave={(e) => (e.currentTarget.style.textDecoration = "none")}>
            {addr}
          </a>
        </span>
      ))}
    </>
  );
}

// 외부 메일 본문의 원격 리소스(추적 픽셀·이미지)를 차단/해제 + 스크립트 제거.
// HTTPS 페이지에서 http:// 리소스를 불러오며 생기는 Mixed Content 경고를 없애고,
// 열람 추적(read-receipt)·XSS를 막는다.
/** 보낸사람이 파일 대신 주소만 보낸 '링크 첨부' 한 건. */
type ExtFileLink = { url: string; host: string; label: string };

// 큰 파일을 메일에 싣지 않고 내려받기 주소만 보내는 곳들.
//
// 네이버·다음·korea.kr 같은 메일은 대용량 첨부를 `bigfile.mail.<도메인>` 으로 내보낸다.
// 그 밖에는 클라우드 저장소 주소를 붙이는 경우가 많다. 본문의 아무 링크나 첨부로
// 세우면 광고 메일이 칩으로 도배되므로, **파일을 주는 곳만** 골라 둔다.
const CLOUD_FILE_HOSTS = [
  "drive.google.com", "dropbox.com", "1drv.ms", "onedrive.live.com",
  "wetransfer.com", "we.tl", "box.com", "mega.nz", "mybox.naver.com",
  "filebox.daum.net", "sendanywhe.re", "send-anywhere.com",
];

function isFileLinkHost(raw: string): boolean {
  const host = (raw || "").toLowerCase().replace(/^www\./, "");
  // bigfile.mail.naver.com · bigfile.mail.korea.kr · largefile.* …
  if (/(^|\.)(bigfile|largefile|bigmail)\./.test(host)) return true;
  if (/(^|\.)sharepoint\.com$/.test(host)) return true;
  return CLOUD_FILE_HOSTS.some((d) => host === d || host.endsWith("." + d));
}

// 링크 옆에 적혀 있는 파일 이름("제2회 … 기본계획.hwpx (25 MB)")을 집어 칩 이름으로 쓴다.
// 못 찾으면 그냥 '대용량 첨부파일' 로 둔다 — 이름이 틀려도 받는 데는 지장이 없다.
const FILE_EXT_RE =
  /\.(?:hwpx?|docx?|xlsx?|pptx?|pdf|txt|csv|rtf|zip|7z|rar|alz|egg|tar|gz|jpe?g|png|gif|webp|bmp|tiff?|svg|ai|psd|eps|indd|cdr|dwg|dxf|cad|showx?|mp3|wav|m4a|flac|aac|ogg|mp4|mov|avi|mkv|wmv|webm)(?=[\s)\],·<>"']|$)/i;

// 글 속 주소. 아웃룩은 인용할 때 "[아이콘주소] 파일명<공유주소>" 꼴로 적는다 — 앞뒤 꺾쇠·대괄호째 지운다.
// 괄호 안의 주소는 닫는 괄호에서 끊는다 — "]미래 콘티.pptx" 처럼 바로 붙은 파일명 첫 단어를 먹지 않게.
const URL_IN_TEXT_RE = /\[https?:\/\/[^\]\s]*\]?|<https?:\/\/[^>\s]*>?|https?:\/\/\S+/gi;

/**
 * 링크 주소 끝의 쓰레기 꼬리를 자른다. 아웃룩 웹 자동 링크는 href 끝에 '>​'(꺾쇠+보이지 않는
 * 글자)를 붙여 두어, 그대로 열면 SharePoint 가 "파일을 찾을 수 없음" 을 띄웠다.
 */
function cleanHref(raw: string): string {
  return (raw || "").trim()
    .replace(/(?:%3E|%E2%80%8B|%EF%BB%BF)+$/i, "")
    .replace(/[>\u200b-\u200d\ufeff\s]+$/, "");
}

// SharePoint 공유 주소의 종류 글자(/:p:/ 등). 이름을 못 찾았을 때 무슨 파일인지라도 보이게.
const SP_KIND: Record<string, string> = {
  w: "Word 문서", x: "Excel 문서", p: "PowerPoint 문서", b: "PDF 문서", o: "OneNote",
  f: "공유 폴더", i: "사진", v: "동영상", u: "파일",
};
function cloudKindLabel(url: string): string {
  const k = url.match(/sharepoint\.com\/:([a-z]):\//i)?.[1]?.toLowerCase();
  return k ? SP_KIND[k] || "" : "";
}

/** 보낸 곳에서 열지 않고 바로 받는 주소. 지원하지 않는 곳이면 null(누르면 열기만). */
function directDownloadUrl(raw: string): string | null {
  try {
    const u = new URL(raw);
    const h = u.hostname.toLowerCase();
    if (/(^|\.)sharepoint\.com$/.test(h)) {
      if (/^\/:f:\//i.test(u.pathname)) return null;      // 폴더는 통째로 받을 수 없다
      u.searchParams.set("download", "1");
      return u.href;
    }
    if (h === "drive.google.com") {
      const id = u.pathname.match(/\/file\/d\/([^/]+)/)?.[1] || u.searchParams.get("id");
      return id ? `https://drive.google.com/uc?export=download&id=${encodeURIComponent(id)}` : null;
    }
    if (h === "dropbox.com" || h.endsWith(".dropbox.com")) {
      u.searchParams.set("dl", "1");
      return u.href;
    }
  } catch { /* 주소가 아니면 받기 단추 없음 */ }
  return null;
}

/** 한 칸(또는 한 줄)의 글에서 파일 이름을 꺼낸다. 이름 같지 않으면 빈 글자.
 *
 * 메일마다 이름 옆에 군더더기가 붙는다 — 네이버는 "(oi) 파일명.ai 1477508335",
 * 관공서는 "파일명.hwpx (25 MB)", 보관함 링크는 "파일명.zip ~ 2026/10/09" 처럼.
 * **확장자에서 끊고** 앞에 붙은 짧은 표시말만 떼면 대부분 깨끗해진다.
 */
function pickFileName(raw: string): string {
  // 주소는 먼저 지운다 — 아이콘 그림 주소(…/pptx.png)를 파일 이름으로 집던 일이 있었다.
  const t = (raw || "").replace(URL_IN_TEXT_RE, " ").replace(/[​-‍﻿]/g, "")
    .replace(/\s+/g, " ").trim().replace(/^(>\s*)+/, "");   // 글자 인용 표시 '> ' 는 이름이 아니다
  if (!t || t.length > 200) return "";
  const m = FILE_EXT_RE.exec(t);
  if (!m) return "";
  const name = t
    .slice(0, m.index + m[0].length)                     // 확장자 뒤(용량·기한)는 버린다
    .replace(/^[([{（][^)\]}）]{0,8}[)\]}）]\s*/, "")      // 앞의 "(oi)" 같은 표시말
    .replace(/^(첨부파일|대용량 첨부파일|첨부|파일)\s*[:：]?\s*/, "")
    .trim();
  if (!name || name.length < 3) return "";
  // 너무 길면 뒤를 남긴다 — 확장자가 보여야 무슨 파일인지 안다.
  return name.length > 60 ? "…" + name.slice(-59) : name;
}

/**
 * 링크 가까이에 적힌 파일 이름을 찾는다.
 *
 * 대용량 첨부는 '저장' 단추와 파일 이름이 **다른 칸**에 들어 있는 일이 흔하다
 * (관공서 메일은 표의 다른 줄에 적는다). 그래서 링크 글자만 보면 'PC에 저장' 만
 * 집힌다. 위로 한 칸씩 넓혀 가며 **가장 안쪽 칸**들의 글을 보고, 너무 넓어지면
 * 멈춘다 — 넓게 훑으면 다른 첨부의 이름을 엉뚱하게 가져온다.
 */
/** 링크 바로 앞, 같은 줄(앞의 줄바꿈·블록 전까지)의 글. 인라인 부모(span 등)는 타고 올라간다. */
function textBeforeOnLine(a: Element): string {
  const BLOCK = /^(BR|DIV|P|TD|TH|TR|LI|UL|OL|TABLE|BLOCKQUOTE|H[1-6]|HR)$/;
  let out = "";
  let node: Node | null = a;
  while (node && out.length < 300) {
    for (let prev = node.previousSibling; prev; prev = prev.previousSibling) {
      if (BLOCK.test(prev.nodeName)) return out;
      out = (prev.textContent || "") + out;
    }
    node = node.parentNode;
    if (!node || BLOCK.test(node.nodeName)) break;
  }
  return out;
}

function fileNameNear(a: Element): string {
  // 링크 글자 → title → 같은 줄 앞의 글 → 같은 칸 그림의 alt 순으로 본다. 대용량 첨부
  // 상자는 이름을 title·alt 로만 두고, 아웃룩 인용은 "파일명<주소>" 처럼 바로 앞에 적는다.
  const own = pickFileName(a.textContent || "")
    || pickFileName(a.getAttribute("title") || "");
  if (own) return own;
  // 같은 줄에 파일이 여럿이면 이 링크 바로 앞의 것이 제 이름이다 — 뒤에서부터 본다.
  const segs = textBeforeOnLine(a).replace(URL_IN_TEXT_RE, "\n").split("\n");
  for (let i = segs.length - 1; i >= 0; i -= 1) {
    const n = pickFileName(segs[i]);
    if (n) return n;
  }
  const row = a.closest("tr, li, p, div") || a.parentElement;
  for (const img of Array.from(row?.querySelectorAll("img[alt]") || [])) {
    const alt = pickFileName(img.getAttribute("alt") || "");
    if (alt) return alt;
  }
  const CELL = "td, th, li, p, span, b, strong, div";
  let scope: Element | null = a.parentElement;
  for (let up = 0; scope && up < 6; up += 1, scope = scope.parentElement) {
    for (const cell of Array.from(scope.querySelectorAll(CELL))) {
      if (cell.querySelector(CELL)) continue;        // 가장 안쪽 칸만 본다
      const name = pickFileName(cell.textContent || "");
      if (name) return name;
    }
    const near = pickFileName(scope.textContent || "");
    if (near) return near;
    if ((scope.textContent || "").length > 600) break;
  }
  return "";
}

/** 같은 파일인지 가리는 열쇠 — SharePoint 는 ?e=… 꼬리만 다른 주소가 같은 파일이다. */
function fileLinkKey(url: string): string {
  try {
    const u = new URL(url);
    return /(^|\.)sharepoint\.com$/i.test(u.hostname) ? u.origin + u.pathname : u.href;
  } catch {
    return url;
  }
}

/** 본문 속 우리 대용량 링크(/mail/dl/<토큰>). */
const DL_LINK_RE = /https?:\/\/[^\s"'<>]*?\/mail\/dl\/([A-Za-z0-9_-]{8,})/g;

// 인용(예전 메일)이 시작되는 표시. 답장은 아래에 예전 메일을 통째로 붙이므로, 이 표시
// 위쪽만 '이 메일에서 새로 쓴 부분' 이다. 상자(인용 블록·지메일·아웃룩)이거나 머리글 글자
// (원본 메일 줄·From:/보낸사람: 줄·TimBox 인용 캡션) 중 **먼저 나오는 것**에서 자른다.
const QUOTE_SEL =
  "blockquote, .gmail_quote, #divRplyFwdMsg, #appendonsend, .OutlookMessageHeader, #mail-editor-reference-message-container";
const QUOTE_TEXT_RE =
  /-{3,}\s*(원본 메일|전달된 메일|Original Message|원본 메시지|Forwarded message|전달된 메시지)|^\s*(From|보낸\s?사람)\s*:|^\s*(원본 메일|전달된 메일)\s*$/i;

/** 본문에서 인용(예전 메일) 앞까지만 — '이 메일에서 새로 쓴 부분'. 표시가 없으면 전부. */
function freshPart(html: string, text: string): { html: string; text: string } {
  let freshHtml = html;
  if (html) {
    try {
      const doc = new DOMParser().parseFromString(html, "text/html");
      let marker: Node | null = doc.querySelector(QUOTE_SEL);
      const walk = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT);
      for (let n = walk.nextNode(); n; n = walk.nextNode()) {
        if (!QUOTE_TEXT_RE.test(n.textContent || "")) continue;
        if (!marker || (n.compareDocumentPosition(marker) & Node.DOCUMENT_POSITION_FOLLOWING)) marker = n;
        break;
      }
      if (marker && doc.body.lastChild) {
        const r = doc.createRange();
        r.setStartBefore(marker);
        r.setEndAfter(doc.body.lastChild);
        r.deleteContents();
        freshHtml = doc.body.innerHTML;
      }
    } catch {
      /* 못 나누면 전부 새로 쓴 것으로 본다 */
    }
  }
  const lines = (text || "").split(/\r?\n/);
  const at = lines.findIndex((l) => QUOTE_TEXT_RE.test(l) || /^\s*>/.test(l));
  return { html: freshHtml, text: at >= 0 ? lines.slice(0, at).join("\n") : text };
}

/** 본문에서 '링크로 온 첨부'(대용량 첨부·클라우드 공유 주소)를 찾는다. */
function extractFileLinks(bodyHtml: string, bodyText: string): ExtFileLink[] {
  const out = new Map<string, ExtFileLink>();
  const add = (raw: string, label: string) => {
    let u: URL;
    try {
      u = new URL(cleanHref(raw));
    } catch {
      return;                              // 주소 모양이 아니면 버린다
    }
    if (!/^https?:$/.test(u.protocol) || !isFileLinkHost(u.hostname)) return;
    // SharePoint 는 같은 파일 주소에 붙는 ?e=… 같은 꼬리만 달라 두 번 잡혔다 — 경로로 묶는다.
    const key = fileLinkKey(u.href);
    const had = out.get(key);
    // 이름이 있는 쪽, 같은 파일이면 더 온전한 이름을 남긴다 — 아웃룩 인용은 줄을 꺾어
    // "대내외<br>사업 환경과….pptx" 처럼 앞 단어가 떨어져 한쪽에선 잘린 이름이 잡힌다.
    const better = !had || (!had.label && !!label)
      || (label.length > had.label.length && label.endsWith(had.label));
    if (!better) return;
    out.set(key, { url: u.href, host: u.hostname.replace(/^www\./, ""), label });
  };

  if (bodyHtml) {
    try {
      const doc = new DOMParser().parseFromString(bodyHtml, "text/html");
      doc.querySelectorAll("a[href]").forEach((a) => {
        add(a.getAttribute("href") || "", fileNameNear(a));
      });
    } catch {
      /* 본문이 깨져 있으면 링크 칩만 포기한다 */
    }
  }
  // 글자뿐인 본문 — 파일 이름은 보통 주소 바로 윗줄·아랫줄에 적혀 있다.
  const lines = (bodyText || "").split(/\r?\n/);
  lines.forEach((line, i) => {
    for (const m of line.matchAll(/https?:\/\/[^\s"'<>)\]]+/g)) {
      const name = pickFileName(line)
        || pickFileName(lines[i - 1] || "")
        || pickFileName(lines[i + 1] || "");
      add(m[0], name);
    }
  });
  // 같은 파일을 두 군데로 걸어 두는 메일이 많다(네이버는 '내 PC 저장' 과
  // 'MYBOX 저장' 두 주소를 나란히 넣는다). 이름이 같으면 먼저 나온 것 하나만
  // 남긴다 — 앞쪽이 대개 바로 받는 주소다.
  const once = new Map<string, ExtFileLink>();
  for (const l of out.values()) {
    const key = l.label || l.url;
    if (!once.has(key)) once.set(key, l);
  }
  return [...once.values()];
}

function processMailHtml(
  html: string, allowRemote: boolean, cidMap: Record<string, string> = {},
): { html: string; blocked: boolean; hasRemote: boolean } {
  let blocked = false;
  let hasRemote = false;
  try {
    const doc = new DOMParser().parseFromString(html, "text/html");
    // ── 위험 요소 제거 (원격 이미지 허용 여부와 무관하게 **항상**) ──────────────
    //
    // 예전에는 태그 몇 개만 지웠다. 그런데 그것만으로는 아무것도 못 막는다 —
    // 실제로 확인해 보니 onerror·onload·onmouseover·javascript: URL·form·base 가
    // 전부 그대로 살아남았다. 메일은 아무나 보낼 수 있으므로, 이건 곧 '남이 보낸
    // 메일을 열기만 하면 내 세션에서 스크립트가 도는' 상태였다. <svg onload> 는
    // 이미지 차단을 켜 놔도 실행되므로 설정과 상관없이 모두가 노출돼 있었다.
    //
    // svg/math: 메일 본문에 거의 쓰이지 않는데 이벤트 실행 경로가 많다.
    // form:     메일 안에서 비밀번호를 받아 밖으로 보내는 전형적인 피싱 수법.
    // base:     상대경로를 통째로 남의 서버로 돌려 버린다.
    doc.querySelectorAll(
      "script, iframe, object, embed, link, meta, svg, math, form, base, noscript",
    ).forEach((el) => el.remove());

    // 이벤트 핸들러 속성(on*)은 정상적인 메일에 있을 이유가 없다. 전부 걷어낸다.
    // 특정 이름만 지우면 새 이벤트가 생길 때마다 뚫리므로 접두어로 지운다.
    doc.querySelectorAll("*").forEach((el) => {
      for (const attr of Array.from(el.attributes)) {
        if (/^on/i.test(attr.name)) el.removeAttribute(attr.name);
      }
    });

    // 스크립트를 실행시키는 URL 스킴을 막는다. 공백·개행·대소문자로 위장할 수 있어
    // 비교 전에 정규화한다 — 스킴 중간에 공백이나 제어문자를 끼워 넣어도
    // 브라우저는 실행하므로, 그대로 비교하면 그냥 지나간다.
    const BAD_SCHEME = /^(javascript|vbscript|data):/i;
    const urlAttrs = ["href", "src", "action", "formaction", "xlink:href", "poster"];
    doc.querySelectorAll("*").forEach((el) => {
      for (const name of urlAttrs) {
        const v = el.getAttribute(name);
        if (!v) continue;
        const norm = v.replace(/[\s\u0000-\u001f]/g, "").toLowerCase();
        // 본문에 심어 둔 인라인 이미지(data:image/...)는 실행 위험이 없어 남긴다.
        if (BAD_SCHEME.test(norm) && !/^data:image\//i.test(norm)) {
          el.removeAttribute(name);
        }
      }
    });

    // 바깥으로 나가는 링크는 새 창으로, opener 연결은 끊는다.
    // (rel 없이 target=_blank 를 두면 열린 쪽에서 이 창을 조작할 수 있다)
    doc.querySelectorAll("a[href]").forEach((a) => {
      a.setAttribute("target", "_blank");
      a.setAttribute("rel", "noopener noreferrer");
      const href = a.getAttribute("href") || "";
      if (/^https?:/i.test(href)) a.setAttribute("href", cleanHref(href));   // 아웃룩 '>​' 꼬리 제거
    });
    // 본문에 적힌 메일 주소를 눌러 바로 쓸 수 있게 링크로 바꾼다.
    // 이미 <a> 안에 있는 글자는 건드리지 않는다(링크 안에 링크가 생기면 깨진다).
    linkifyEmails(doc);

    // 인라인 이미지(cid:)를 미리 받아 둔 blob 주소로 바꿔 끼운다. 원격 이미지가 아니라
    // 이 메일에 딸려 온 파일이므로 차단 대상이 아니다.
    doc.querySelectorAll('img[src^="cid:"]').forEach((img) => {
      const cid = (img.getAttribute("src") || "").slice(4);
      const url = cidMap[cid];
      if (url) img.setAttribute("src", url);
    });
    // 허용 중이어도 원격 리소스 유무는 확인해 둔다 — 다시 차단할지 고를 수 있게.
    doc.querySelectorAll("img").forEach((img) => {
      const src = img.getAttribute("src") || "";
      if (/^(https?:)?\/\//i.test(src) || img.getAttribute("srcset")) hasRemote = true;
    });
    if (!allowRemote) {
      doc.querySelectorAll("img").forEach((img) => {
        const src = img.getAttribute("src") || "";
        if (/^(https?:)?\/\//i.test(src)) {
          img.setAttribute("data-blocked-src", src);
          img.removeAttribute("src");
          // src 만 떼면 이미지가 폭 0 으로 주저앉아, 그 자리를 쓰던 상자가 텅 빈 채로
          // 남는다(구글 드라이브 첨부 칩 같은 것이 '깨진 상자'로 보이던 이유).
          // 자리를 지키는 표시를 남겨 레이아웃을 유지하고, 차단됐다는 것도 보이게 한다.
          img.classList.add("mail-blocked-img");
          blocked = true;
        }
        if (img.getAttribute("srcset")) { img.removeAttribute("srcset"); blocked = true; }
      });
      doc.querySelectorAll("[background]").forEach((el) => { el.removeAttribute("background"); blocked = true; });
      doc.querySelectorAll("[style]").forEach((el) => {
        const s = el.getAttribute("style") || "";
        if (/url\(\s*['"]?(https?:)?\/\//i.test(s)) {
          el.setAttribute("style", s.replace(/url\(\s*['"]?(https?:)?\/\/[^)]*\)/gi, "none"));
          blocked = true;
        }
      });
    }
    return { html: doc.body.innerHTML, blocked, hasRemote };
  } catch {
    return { html, blocked: false, hasRemote: false };
  }
}

/** 확장자·MIME 으로 미리보기 방식을 정한다. 내려받아 열어 보지 않아도 되게 한다. */
type PreviewKind = "image" | "pdf" | "sheet" | "html" | "text" | null;
function previewKind(name: string, mime: string | null): PreviewKind {
  const n = (name || "").toLowerCase();
  const m = (mime || "").toLowerCase();
  if (m.startsWith("image/") || /\.(png|jpe?g|gif|webp|bmp|svg|heic)$/.test(n)) return "image";
  if (m === "application/pdf" || n.endsWith(".pdf")) return "pdf";
  if (/\.(xlsx|xlsm|csv)$/.test(n)) return "sheet";
  // HTML 은 새 탭에 페이지로 연다(세금계산서·카드 명세서·보안메일 — openHtmlAttachment).
  // 예전엔 text/ 로 묶여 코드가 그대로 보였고, 이름만 .html 이고 형식이
  // octet-stream 인 것은 아예 내려받기만 됐다.
  if (m === "text/html" || m === "application/xhtml+xml" || /\.(html?|xhtml)$/.test(n)) return "html";
  if (m.startsWith("text/") || /\.(txt|log|json|md)$/.test(n)) return "text";
  return null;   // 그 외는 내려받기만
}

type Sheet = { name: string; rows: string[][]; truncated: boolean };


/** 첨부 미리보기 창 — 이미지·PDF 는 그대로, 엑셀/CSV 는 서버가 파싱한 표로 보여 준다. */
function AttachmentPreview({ att, onClose }: {
  att: { id: number; file_name: string; mime_type: string | null; file_size: number };
  onClose: () => void;
}) {
  const kind = previewKind(att.file_name, att.mime_type);
  const [url, setUrl] = useState<string>("");
  const [sheets, setSheets] = useState<Sheet[] | null>(null);
  const [text, setText] = useState<string>("");
  const [err, setErr] = useState<string>("");
  const [tab, setTab] = useState(0);

  useEffect(() => {
    let objectUrl = "";
    let alive = true;
    (async () => {
      try {
        if (kind === "sheet") {
          const r = await api.get<{ sheets: Sheet[] }>(`/mail/attachments/${att.id}/sheet`);
          if (alive) setSheets(r.data.sheets);
        } else {
          const r = await api.get(`/mail/attachments/${att.id}`, { responseType: "blob" });
          const blob = r.data as Blob;
          if (kind === "text") {
            const t = await blob.text();
            if (alive) setText(t.slice(0, 200000));
          } else {
            objectUrl = URL.createObjectURL(blob);
            if (alive) setUrl(objectUrl);
          }
        }
      } catch (e: any) {
        if (alive) setErr(e?.response?.data?.error?.message ?? e?.response?.data?.detail ?? "불러오지 못했습니다");
      }
    })();
    return () => { alive = false; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [att.id, kind]);

  useEffect(() => {
    const esc = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [onClose]);

  const download = async () => {
    try {
      const r = await api.get(`/mail/attachments/${att.id}`, { responseType: "blob" });
      const u = URL.createObjectURL(r.data as Blob);
      const a = document.createElement("a");
      a.href = u; a.download = att.file_name; a.click();
      URL.revokeObjectURL(u);
    } catch { toast.error("다운로드 실패"); }
  };

  return (
    <div onClick={onClose}
      style={{ position: "fixed", inset: 0, zIndex: 200, background: "rgba(0,0,0,.55)",
               display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div onClick={(e) => e.stopPropagation()}
        style={{ background: "var(--surface,#fff)", borderRadius: 12, width: "min(1100px, 96vw)",
                 height: "min(88vh, 900px)", display: "flex", flexDirection: "column", overflow: "hidden",
                 boxShadow: "0 20px 60px rgba(0,0,0,.35)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px",
                      borderBottom: "1px solid var(--border)" }}>
          <span style={{ fontWeight: 700, fontSize: 13.5, flex: 1, overflow: "hidden",
                         textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{att.file_name}</span>
          <span style={{ fontSize: 11.5, color: "var(--text-3)" }}>{formatBytes(att.file_size)}</span>
          <button className="t-btn t-btn-sm" onClick={download}>내려받기</button>
          <button className="t-iconbtn" onClick={onClose} title="닫기"><I.X size={16}/></button>
        </div>

        <div style={{ flex: 1, minHeight: 0, overflow: "auto", background: kind === "image" ? "#111827" : "var(--bg-0)" }}>
          {err ? (
            <div style={{ padding: 24, color: "var(--text-3)", fontSize: 13 }}>{err}</div>
          ) : kind === "image" ? (
            url ? <div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", padding: 12 }}>
                    <img src={url} alt={att.file_name} style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}/>
                  </div>
                : <div style={{ padding: 24, color: "var(--text-3)" }}>불러오는 중…</div>
          ) : kind === "pdf" ? (
            url ? <iframe src={url} title={att.file_name} style={{ width: "100%", height: "100%", border: "none" }}/>
                : <div style={{ padding: 24, color: "var(--text-3)" }}>불러오는 중…</div>
          ) : kind === "sheet" ? (
            sheets ? (
              <div>
                {sheets.length > 1 && (
                  <div style={{ display: "flex", gap: 4, padding: "8px 10px 0", flexWrap: "wrap" }}>
                    {sheets.map((s, i) => (
                      <button key={s.name + i} onClick={() => setTab(i)}
                        style={{ fontSize: 12, padding: "4px 10px", borderRadius: 6, cursor: "pointer",
                                 border: `1px solid ${i === tab ? "var(--p-400)" : "var(--border)"}`,
                                 background: i === tab ? "rgba(99,102,241,.1)" : "transparent",
                                 color: i === tab ? "var(--p-600)" : "var(--text-2)" }}>{s.name}</button>
                    ))}
                  </div>
                )}
                <table style={{ borderCollapse: "collapse", fontSize: 12.5, margin: 10 }}>
                  <tbody>
                    {(sheets[tab]?.rows ?? []).map((row, ri) => (
                      <tr key={ri} style={{ background: ri === 0 ? "var(--panel)" : undefined }}>
                        {row.map((cell, ci) => (
                          <td key={ci} style={{ border: "1px solid var(--border)", padding: "4px 8px",
                                                whiteSpace: "nowrap", fontWeight: ri === 0 ? 700 : 400 }}>{cell}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
                {sheets[tab]?.truncated && (
                  <div style={{ padding: "0 12px 12px", fontSize: 11.5, color: "var(--text-3)" }}>
                    앞부분만 표시했습니다 — 전체 내용은 내려받아 확인하세요.
                  </div>
                )}
              </div>
            ) : <div style={{ padding: 24, color: "var(--text-3)" }}>불러오는 중…</div>
          ) : kind === "text" ? (
            <pre style={{ margin: 0, padding: 14, fontSize: 12.5, lineHeight: 1.6, whiteSpace: "pre-wrap",
                          fontFamily: "ui-monospace, monospace" }}>{text}</pre>
          ) : null}
        </div>
      </div>
    </div>
  );
}

/** 본문에 심어 보낸 대용량 첨부 링크. 내가 만든 것만 상세 정보가 채워진다. */
type FileLinkInfo = {
  token: string;
  url?: string;
  id?: number;
  file_name?: string;
  file_size?: number;
  expires_at?: string;
  download_count?: number;
  revoked_at?: string | null;
};

type Props = {
  mail: MailDetail;
  labels: MailLabel[];
  onClose: () => void;
  onChange: () => void;        // re-fetch list
  onReply: (m: MailDetail) => void;
  onReplyAll: (m: MailDetail) => void;
  onForward: (m: MailDetail) => void;
  onResend: (m: MailDetail) => void;
  /** 예약 메일 '수정' — 작성 화면으로 연다(임시 메일 편집과 같은 길). */
  onEditDraft?: (m: MailDetail) => void;
  /** 본문·헤더의 메일 주소를 눌렀을 때 그 사람에게 새 메일을 쓴다. */
  onComposeTo: (addr: string) => void;
  /** 주소 메뉴의 "이 사람 메일 모아보기" — 목록을 그 발신자로 좁힌다. */
  onSearchSender?: (addr: string) => void;
  /** 모바일에서 목록을 잠깐 들춰 볼 때 상세를 숨기는 용도(peek-hidden). */
  className?: string;
};

const ACCESS_TOKEN_HEADER = "Authorization";

function initials(addr: string): string {
  const local = addr.split("@")[0];
  return local.slice(0, 2).toUpperCase();
}

function colorOf(addr: string): string {
  let hash = 0;
  for (let i = 0; i < addr.length; i++) hash = (hash * 31 + addr.charCodeAt(i)) & 0xffffffff;
  const hue = Math.abs(hash) % 360;
  return `hsl(${hue}, 60%, 52%)`;
}

export function MailDetailPane({ mail, labels, onClose, onChange, onReply, onReplyAll, onForward, onResend, onEditDraft, onComposeTo, onSearchSender, className }: Props) {
  const [busy, setBusy] = useState(false);
  const [labelMenuOpen, setLabelMenuOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [preview, setPreview] = useState<MailAttachment | null>(null);
  // 원격 이미지 표시 여부는 **기억한다**.
  // 예전에는 메일을 열 때마다 차단으로 되돌아가, 이미지가 있는 메일마다 매번
  // '이미지 표시' 를 눌러야 했다. 기본은 표시로 두고, 추적이 걱정될 때만 끄면 된다.
  // (스크립트·iframe 제거는 이 설정과 무관하게 항상 적용된다)
  const [showRemote, setShowRemote] = useState<boolean>(() => {
    try { return localStorage.getItem("mail-show-remote") !== "0"; } catch { return true; }
  });
  const setShowRemotePref = (v: boolean) => {
    setShowRemote(v);
    try { localStorage.setItem("mail-show-remote", v ? "1" : "0"); } catch { /* ignore */ }
  };

  useEffect(() => { setLabelMenuOpen(false); }, [mail.id]);   // 원격 이미지 설정은 유지한다

  // 주소 오른쪽 클릭 메뉴(복사·메일 쓰기·답장·주소록·모아보기).
  // 주소를 손으로 긁어 복사하던 일을 없애려는 것 — 헤더의 주소는 링크라 드래그가 잘 안 잡힌다.
  const [addrMenu, setAddrMenu] = useState<{ x: number; y: number; raw: string } | null>(null);
  const openAddrMenu = (raw: string, e: React.MouseEvent) => setAddrMenu({ x: e.clientX, y: e.clientY, raw });
  useEffect(() => { setAddrMenu(null); }, [mail.id]);
  useEffect(() => {
    if (!addrMenu) return;
    const close = () => setAddrMenu(null);
    const onKey = (ev: KeyboardEvent) => { if (ev.key === "Escape") setAddrMenu(null); };
    // mousedown 으로 닫으면 메뉴 항목의 click 이 취소되므로, 항목은 mousedown 에서 실행한다.
    window.addEventListener("mousedown", close);
    window.addEventListener("scroll", close, true);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousedown", close);
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("keydown", onKey);
    };
  }, [addrMenu]);

  // 수신 확인(보낸 메일): 사내는 받은 사본의 읽음 시각, 외부는 열람 픽셀 기록.
  const [receipts, setReceipts] = useState<MailReceipt[] | null>(null);
  // 수신 확인 목록 접기 — 휴대폰에서는 받는 사람 목록이 본문을 화면 밖으로 밀어냈다(요약 한 줄로 시작).
  const [receiptsOpen, setReceiptsOpen] = useState(() => !window.matchMedia("(max-width: 720px)").matches);
  const [receiptsBusy, setReceiptsBusy] = useState(false);
  const loadReceipts = async () => {
    if (mail.folder !== "SENT") { setReceipts(null); return; }
    setReceiptsBusy(true);
    try {
      const r = await api.get<MailReceipt[]>(`/mail/${mail.id}/receipts`);
      setReceipts(r.data || []);
    } catch { setReceipts([]); }
    finally { setReceiptsBusy(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { void loadReceipts(); }, [mail.id, mail.folder]);

  // 본문 안의 인라인 이미지(cid:)를 첨부에서 찾아 실제로 보여 준다.
  //
  // 메일 클라이언트는 본문 이미지를 첨부로 함께 보내고 본문에서는 <img src="cid:..">
  // 로 가리킨다. 그대로 두면 브라우저가 cid 를 모르니 전부 깨져 보인다(첨부 목록에는
  // 있는데 본문에서만 안 보이는 상태). 첨부를 내려받아 blob 주소로 바꿔 끼운다.
  // 첨부 API 는 토큰이 필요해 <img src="/api/..."> 로는 못 부르므로 blob 이 답이다.
  const [cidMap, setCidMap] = useState<Record<string, string>>({});
  // 본문에 그려 넣은 이미지들. 첨부 목록에서는 감춘다 — 서명 로고까지 파일로
  // 늘어놓으면 진짜 첨부가 묻힌다.
  const [inlineIds, setInlineIds] = useState<Set<number>>(new Set());
  useEffect(() => {
    const html = mail.body_html || "";
    const cids = Array.from(html.matchAll(/src=["']cid:([^"']+)["']/gi)).map((m) => m[1]);
    if (!cids.length || !mail.attachments?.length) { setCidMap({}); setInlineIds(new Set()); return; }

    const urls: string[] = [];
    let alive = true;
    (async () => {
      const map: Record<string, string> = {};
      const used = new Set<number>();
      // 오래된 메일은 content_id 를 저장하기 전에 수집돼 값이 없다. 그럴 땐 본문에
      // 나온 순서와 이미지 첨부 순서를 맞춰 짝짓는다(대개 같은 순서로 들어 있다).
      const images = mail.attachments.filter((a) => (a.mime_type || "").startsWith("image/"));
      let fallbackIdx = 0;
      for (const cid of cids) {
        if (map[cid]) continue;
        const byId = mail.attachments.find(
          (a) => a.content_id && a.content_id.replace(/^<|>$/g, "") === cid,
        );
        const att = byId ?? images[fallbackIdx++];
        if (!att) continue;
        try {
          const r = await api.get(`/mail/attachments/${att.id}`, { responseType: "blob" });
          const u = URL.createObjectURL(r.data as Blob);
          urls.push(u);
          map[cid] = u;
          used.add(att.id);
        } catch { /* 한 장이 실패해도 나머지는 보여 준다 */ }
      }
      if (alive) { setCidMap(map); setInlineIds(used); }
      else urls.forEach((u) => URL.revokeObjectURL(u));
    })();
    return () => { alive = false; urls.forEach((u) => URL.revokeObjectURL(u)); };
  }, [mail.id, mail.body_html, mail.attachments]);

  const processed = useMemo(
    () => (mail.body_html ? processMailHtml(mail.body_html, showRemote, cidMap) : null),
    [mail.body_html, showRemote, cidMap],
  );

  // HTML 첨부(세금계산서·카드 명세서·보안메일)는 새 탭에 '페이지' 로 연다.
  // 서버가 격리 머리글(CSP sandbox)을 붙여 내보내므로, 아무나 보낸 HTML 이라도
  // TimBox 로그인 정보에는 손이 닿지 않는다. 새 탭은 로그인 토큰을 실을 수 없어서
  // 먼저 5분짜리 열람 주소를 받아 그 주소로 연다.
  const openHtmlAttachment = async (attId: number) => {
    try {
      const r = await api.post<{ url: string }>(`/mail/attachments/${attId}/view-url`);
      const w = window.open(r.data.url, "_blank");
      if (!w) { toast.error("새 창이 막혔습니다", "주소창 오른쪽에서 이 사이트의 팝업을 허용해 주세요"); return; }
      // 열린 페이지가 이 창(TimBox)을 다른 곳으로 바꿔치지 못하게 연결을 끊는다.
      try { w.opener = null; } catch { /* 다른 출처면 못 건드린다 */ }
    } catch (e: any) {
      toast.error("열기 실패", e?.response?.data?.error?.message ?? "");
    }
  };

  // 첨부 목록에는 '진짜 받아야 할 파일'만 남긴다.
  //
  // 빼는 것 두 가지:
  //  · 본문에 그려 넣은 이미지(cid 로 이어진 것)
  //  · Content-ID 가 붙은 이미지 — 서명 로고·머리글 같은 서식 이미지다. 보내는
  //    쪽에서 본문에 쓰려고 넣은 것이지 받으라고 보낸 파일이 아니다.
  //    (사진을 진짜 첨부하면 Content-ID 가 붙지 않으므로 안전하다)
  const isDecorative = (a: MailAttachment) =>
    inlineIds.has(a.id) || (!!a.content_id && (a.mime_type || "").startsWith("image/"));
  const realAttachments = useMemo(
    () => (mail.attachments || []).filter((a) => !isDecorative(a)),
    [mail.attachments, inlineIds],
  );
  const hiddenCount = (mail.attachments || []).length - realAttachments.length;

  // ── 대용량 첨부 링크 ────────────────────────────────────────────────────────
  // 링크로 보낸 메일은 첨부 행이 없다. 그래서 보낸메일함에서 열어도 아무 흔적이 없고
  // 본문 글자 속 주소만 남아, "링크가 안 만들어졌다"고 보였다. 본문에서 우리 링크
  // 주소를 찾아 칩으로 세워 준다 — 기한·받아간 횟수가 보이고 회수도 여기서 한다.
  // 본문에 실제로 실린 **전체 주소**를 잡는다. 토큰만 떼어 두면 칩에 걸 주소를
  // 다시 조립해야 하는데, 그 사이 도메인 설정이 달라지면 엉뚱한 곳을 가리킨다.
  const bodyLinks = useMemo(() => {
    const hay = `${mail.body_text || ""} ${mail.body_html || ""}`;
    const out = new Map<string, string>();   // token -> url
    for (const m of hay.matchAll(DL_LINK_RE)) {
      if (!out.has(m[1])) out.set(m[1], m[0]);
    }
    return [...out].map(([token, url]) => ({ token, url }));
  }, [mail.body_text, mail.body_html]);

  const [fileLinks, setFileLinks] = useState<FileLinkInfo[]>([]);
  useEffect(() => {
    if (!bodyLinks.length) { setFileLinks([]); return; }
    let alive = true;
    // 파일명·용량·기한은 토큰으로 조회한다. file-links 목록은 '내가 만든 것'만
    // 돌려주므로, 공용 편지함이나 남이 보낸 메일에서는 칩이 이름 없이 떴다.
    // 회수 버튼에 필요한 id 만 그 목록에서 따로 채운다(만든 사람만 회수할 수 있다).
    Promise.all([
      Promise.all(bodyLinks.map((b) =>
        api.get<FileLinkInfo>(`/mail/dl/${b.token}/meta`)
          .then((r) => ({ ...r.data, ...b }))
          .catch(() => ({ ...b } as FileLinkInfo)),
      )),
      api.get<FileLinkInfo[]>("/mail/file-links").then((r) => r.data || []).catch(() => []),
    ]).then(([metas, mine]) => {
      if (!alive) return;
      const idByToken = new Map(mine.map((l) => [l.token, l.id]));
      setFileLinks(metas.map((m) => ({ ...m, id: idByToken.get(m.token) })));
    });
    return () => { alive = false; };
  }, [bodyLinks]);

  const revokeLink = async (l: FileLinkInfo) => {
    if (!l.id) return;
    if (!window.confirm(`'${l.file_name}' 링크를 회수할까요?
이미 보낸 메일의 주소도 더는 열리지 않습니다.`)) return;
    try {
      await api.delete(`/mail/file-links/${l.id}`);
      setFileLinks((cur) => cur.map((x) => (x.id === l.id ? { ...x, revoked_at: new Date().toISOString() } : x)));
      toast.success("링크를 회수했습니다");
    } catch (e: any) {
      toast.error("회수 실패", e?.response?.data?.error?.message ?? "");
    }
  };
  // ── 밖에서 온 '링크 첨부' ──────────────────────────────────────────────────
  //
  // 큰 파일은 메일에 실리지 않고 "내려받기 주소"만 오는 일이 흔하다(네이버·다음·관공서
  // 메일의 대용량 첨부, 구글 드라이브 링크 등). 그러면 첨부 줄이 비어 있고, 본문의
  // '저장' 단추는 원격 이미지라 차단되면 빈 칸으로 남아 "첨부가 안 왔다" 처럼 보였다.
  // 본문에서 그 주소를 찾아 첨부 줄에 칩으로 세우고, 누르면 그곳으로 바로 보낸다.
  // 답장·대댓글은 아래에 예전 메일을 통째로 인용한다. 그 속 링크까지 '이 메일의 첨부' 로
  // 세우면 헷갈려서, 새로 쓴 부분에 있는 것만 위에 두고 인용 속 것은 '이전 메일' 로 따로 접는다.
  // 전달(Fwd·FW) 메일은 전달한 내용이 곧 본문이라 나누지 않는다.
  const fresh = useMemo(
    () => (/^\s*(fwd?|전달)\s*[:：]/i.test(mail.subject || "") ? null
      : freshPart(mail.body_html || "", mail.body_text || "")),
    [mail.subject, mail.body_html, mail.body_text]);
  const [externalLinks, oldExternalLinks] = useMemo(() => {
    const all = extractFileLinks(mail.body_html || "", mail.body_text || "");
    if (!fresh) return [all, [] as ExtFileLink[]];
    const mine = new Set(extractFileLinks(fresh.html, fresh.text).map((l) => fileLinkKey(l.url)));
    return [all.filter((l) => mine.has(fileLinkKey(l.url))), all.filter((l) => !mine.has(fileLinkKey(l.url)))];
  }, [mail.body_html, mail.body_text, fresh]);
  const freshTokens = useMemo(
    () => (fresh ? new Set([...`${fresh.text} ${fresh.html}`.matchAll(DL_LINK_RE)].map((m) => m[1])) : null),
    [fresh]);
  const curFileLinks = freshTokens ? fileLinks.filter((l) => freshTokens.has(l.token)) : fileLinks;
  const oldFileLinks = freshTokens ? fileLinks.filter((l) => !freshTokens.has(l.token)) : [];
  const [showOld, setShowOld] = useState(false);

  const [showHidden, setShowHidden] = useState(false);
  // 첨부 목록 접기. 휴대폰에서는 칩이 화면을 다 차지해 본문이 안 보였다 — 좁은 화면은 접힌 채로 시작.
  const [attOpen, setAttOpen] = useState(() => !window.matchMedia("(max-width: 720px)").matches);
  const attCount = realAttachments.length + curFileLinks.length + externalLinks.length;
  const oldCount = oldFileLinks.length + oldExternalLinks.length;

  // 링크 첨부 칩 — 이 메일의 것과 이전 메일(인용)의 것을 같은 모양으로 그린다.
  const renderLinkChips = (files: FileLinkInfo[], exts: ExtFileLink[], hints: boolean) => (
    <>
          {files.length > 0 && (
            <div className="attachments" style={{ paddingTop: 10 }}>
              {hints && <div className="att-hint" style={{ width: "100%", fontSize: 11.5, color: "var(--text-3)", marginBottom: 2 }}>
                대용량 첨부 링크 — 파일은 서버에 있고 본문에는 주소만 실렸습니다.
              </div>}
              {files.map((l) => {
                const dead = !!l.revoked_at || (l.expires_at ? new Date(l.expires_at) <= new Date() : false);
                return (
                  <a key={l.token} className="att-chip"
                    href={dead ? undefined : l.url}
                    target="_blank" rel="noopener noreferrer"
                    title={dead ? "더는 열리지 않는 링크입니다" : "받기 페이지 열기"}
                    onClick={(e) => { if (dead) e.preventDefault(); }}
                    style={{ cursor: dead ? "default" : "pointer", opacity: dead ? 0.55 : 1,
                             textDecoration: dead ? "line-through" : "none",
                             color: "var(--text-2)" }}>
                    <I.Paper size={14}/> <span className="att-name">{l.file_name || "대용량 첨부"}</span>
                    {l.file_size ? <span className="size">{formatBytes(l.file_size)}</span> : null}
                    {l.expires_at && !dead && (
                      <span style={{ fontSize: 11, color: "var(--text-3)" }}>
                        {new Date(l.expires_at).toLocaleDateString("ko-KR", { month: "long", day: "numeric" })}까지
                      </span>
                    )}
                    {typeof l.download_count === "number" && (
                      <span style={{ fontSize: 11, color: "var(--text-3)" }}>· {l.download_count}회 받음</span>
                    )}
                    {l.revoked_at && <span style={{ fontSize: 11, color: "var(--danger,#ef4444)" }}>회수됨</span>}
                    {l.id && !dead && (
                      <button
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); void revokeLink(l); }}
                        title="이 링크를 더는 열리지 않게 합니다"
                        style={{ marginLeft: 4, border: "none", background: "transparent",
                                 color: "var(--danger,#ef4444)", cursor: "pointer", fontSize: 11 }}>
                        회수
                      </button>
                    )}
                  </a>
                );
              })}
            </div>
          )}

          {exts.length > 0 && (
            <div className="attachments" style={{ paddingTop: 10 }}>
              {hints && <div className="att-hint" style={{ width: "100%", fontSize: 11.5, color: "var(--text-3)", marginBottom: 2 }}>
                링크로 온 첨부 — 이름을 누르면 보낸 곳에서 열리고, ⬇ 를 누르면 바로 내려받습니다.
                보낸 회사가 본인 확인을 요구하면 이 메일 주소로 온 인증 코드를 넣으세요.
              </div>}
              {exts.map((l) => {
                const dl = directDownloadUrl(l.url);
                return (
                  <span key={l.url} className="att-chip" style={{ color: "var(--text-2)" }}>
                    <a className="att-main" href={l.url} target="_blank" rel="noopener noreferrer"
                      title={`보낸 곳에서 열기\n${l.url}`}
                      style={{ display: "inline-flex", alignItems: "center", gap: 6,
                               color: "inherit", textDecoration: "none", cursor: "pointer" }}>
                      <I.Cloud size={14}/> <span className="att-name">{l.label || cloudKindLabel(l.url) || "대용량 첨부파일"}</span>
                      <span className="size">{l.host}</span>
                    </a>
                    {dl && (
                      <a className="att-dl" href={dl} target="_blank" rel="noopener noreferrer" title="바로 내려받기"
                        style={{ marginLeft: 6, padding: "0 6px", borderLeft: "1px solid var(--border)",
                                 color: "var(--p-600)", textDecoration: "none", fontWeight: 700 }}>⬇</a>
                    )}
                  </span>
                );
              })}
            </div>
          )}
    </>
  );

  // 패널 밖(목록/도구막대 등)을 클릭하면 상세보기를 닫는다. (딤 없이)
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      const t = e.target as HTMLElement | null;
      if (t && t.closest(".mail-detail")) return;   // 패널 내부 클릭은 무시
      onClose();
    };
    const id = window.setTimeout(() => document.addEventListener("mousedown", onDown), 0);
    return () => { window.clearTimeout(id); document.removeEventListener("mousedown", onDown); };
  }, [onClose]);

  const star = async () => {
    setBusy(true);
    try {
      await api.patch(`/mail/${mail.id}`, { is_starred: !mail.is_starred });
      onChange();
    } finally { setBusy(false); }
  };

  const important = async () => {
    setBusy(true);
    try {
      await api.patch(`/mail/${mail.id}`, { is_important: !mail.is_important });
      onChange();
    } finally { setBusy(false); }
  };

  const trash = async () => {
    setBusy(true);
    try {
      await api.delete(`/mail/${mail.id}`);
      toast.success(mail.folder === "TRASH" ? "영구 삭제" : "휴지통으로 이동");
      onChange(); onClose();
    } catch (e: any) {
      toast.error("삭제 실패", e?.response?.data?.error?.message);
    } finally { setBusy(false); }
  };

  // 예약 메일 바로 보내기 — 예약 시각을 지운 뒤 초안 보내기 길로 보낸다(시각이 남아 있으면 다시 예약된다).
  const sendScheduledNow = async () => {
    if (!window.confirm("예약을 풀고 지금 바로 보낼까요?")) return;
    setBusy(true);
    try {
      await api.patch(`/mail/drafts/${mail.id}`, { scheduled_at: null });
      const r = await api.post(`/mail/drafts/${mail.id}/send`, {}, { timeout: 0 });
      const failed = (Array.isArray(r.data) ? r.data : []).find((m: any) => m?.delivery_error);
      if (failed) toast.error("보내지 못했습니다", String(failed.delivery_error).slice(0, 160));
      else toast.success("발송 완료");
      onChange(); onClose();
    } catch (e: any) {
      toast.error("발송 실패", e?.response?.data?.error?.message ?? "");
    } finally { setBusy(false); }
  };

  const unread = async () => {
    setBusy(true);
    try {
      await api.patch(`/mail/${mail.id}`, { is_read: false });
      onChange();
      onClose();
    } finally { setBusy(false); }
  };

  const move = async (folder: string) => {
    setBusy(true);
    try {
      await api.patch(`/mail/${mail.id}`, { folder });
      toast.success(`${folder}(으)로 이동`);
      onChange();
    } finally { setBusy(false); }
  };

  const toggleLabel = async (l: MailLabel) => {
    const has = mail.labels.some((x) => x.id === l.id);
    const next = has ? mail.labels.filter((x) => x.id !== l.id) : [...mail.labels, l];
    setBusy(true);
    try {
      await api.post(`/mail/${mail.id}/labels`, { label_ids: next.map((x) => x.id) });
      onChange();
    } finally { setBusy(false); }
  };

  // 인자를 주면 그 주소를, 없으면 발신자를 주소록에 넣는다(받는사람·참조에서도 쓰려고).
  const addToContacts = async (rawAddr?: string) => {
    const raw = rawAddr ?? mail.from_addr ?? "";
    const { name, email } = splitAddr(raw);
    if (!email.includes("@")) { toast.warn("유효한 메일 주소가 아닙니다"); return; }
    try {
      await api.post("/mail/contacts/", { name, email });
      toast.success("주소록에 추가했습니다", email);
    } catch (e: any) {
      toast.error("주소록 추가 실패", e?.response?.data?.error?.message);
    }
  };

  const downloadAtt = async (attId: number, name: string) => {
    try {
      const r = await api.get(`/mail/attachments/${attId}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data as Blob);
      const a = document.createElement("a");
      a.href = url; a.download = name; a.click();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      toast.error("다운로드 실패", e?.response?.data?.error?.message);
    }
    void ACCESS_TOKEN_HEADER;
  };

  return (
    <div className={`t-detail mail-detail ${expanded ? "expanded" : ""} ${className || ""}`}>
        <div className="head">
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <h2 style={{ flex: 1 }}>{mail.subject || "(제목 없음)"}</h2>
            <button className="t-btn t-btn-sm" onClick={() => setExpanded(!expanded)}
              title="전체 화면으로 보기" style={{ flexShrink: 0 }}>
              {expanded ? "↩ 작게" : "⛶ 크게 보기"}
            </button>
            <button className="t-iconbtn" onClick={star} title="별표" disabled={busy}>
              {mail.is_starred ? <I.StarF size={18} style={{ color: "#F59E0B" }}/> : <I.Star size={18}/>}
            </button>
            <button className="t-iconbtn" onClick={important} title="중요" disabled={busy}>
              <I.AlertC size={18} style={{ color: mail.is_important ? "var(--danger)" : undefined }}/>
            </button>
            <button className="t-btn t-btn-sm" onClick={onClose} title="메일 보기 닫기" style={{ flexShrink: 0 }}>
              ✕ 닫기
            </button>
          </div>

          {mail.folder === "SCHEDULED" && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", margin: "8px 0 4px",
                          padding: "10px 12px", borderRadius: 10, background: "var(--p-50, rgba(99,102,241,.08))",
                          border: "1px solid var(--p-200, rgba(99,102,241,.25))", fontSize: 13 }}>
              <span style={{ flex: 1, minWidth: 180 }}>
                ⏰ <b>{mail.scheduled_at ? fmtFullDate(mail.scheduled_at) : "예약된 시각"}</b>에 보낼 예정입니다.
              </span>
              {onEditDraft && (
                <button className="t-btn t-btn-sm" disabled={busy} onClick={() => onEditDraft(mail)}>✏️ 수정</button>
              )}
              <button className="t-btn t-btn-sm t-btn-primary" disabled={busy} onClick={() => void sendScheduledNow()}>
                지금 보내기
              </button>
              <button className="t-btn t-btn-sm" disabled={busy}
                onClick={() => { if (window.confirm("예약을 취소할까요? 메일은 휴지통으로 옮겨집니다.")) void trash(); }}
                style={{ color: "var(--danger,#ef4444)" }}>
                예약 취소
              </button>
            </div>
          )}

          <div className="from-line">
            <div className="av" style={{ background: colorOf(mail.from_addr) }}>{initials(mail.from_addr)}</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 600, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                {/* 발신자를 눌러 바로 답장 성격의 새 메일을 쓸 수 있게 한다. */}
                <a href={`mailto:${mail.from_addr}`}
                   onClick={(e) => { e.preventDefault(); onComposeTo(mail.from_addr); }}
                   onContextMenu={(e) => { e.preventDefault(); openAddrMenu(mail.from_addr, e); }}
                   title="클릭: 메일 쓰기 / 오른쪽 클릭: 복사·답장·주소록 등"
                   style={{ color: "inherit", textDecoration: "none", cursor: "pointer" }}
                   onMouseEnter={(e) => (e.currentTarget.style.textDecoration = "underline")}
                   onMouseLeave={(e) => (e.currentTarget.style.textDecoration = "none")}>
                  {mail.from_addr}
                </a>
                <button className="t-btn t-btn-sm t-btn-outline" onClick={() => void addToContacts()}
                  title="발신자를 주소록에 추가" style={{ padding: "1px 8px", fontSize: 11, fontWeight: 500 }}>
                  + 주소록
                </button>
              </div>
              {/* 받는사람·참조도 각각 눌러 그 사람에게 새 메일을 쓸 수 있다. */}
              <div className="meta">
                받는사람: <AddrList list={mail.to_addrs} onComposeTo={onComposeTo} onMenu={openAddrMenu}/>
                {mail.cc_addrs.length > 0 && (
                  <> · 참조: <AddrList list={mail.cc_addrs} onComposeTo={onComposeTo} onMenu={openAddrMenu}/></>
                )}
                {mail.bcc_addrs && mail.bcc_addrs.length > 0 && (
                  <> · 숨은참조: <AddrList list={mail.bcc_addrs} onComposeTo={onComposeTo} onMenu={openAddrMenu}/></>
                )}
              </div>
              <div className="meta">{fmtFullDate(mail.received_at || mail.sent_at || mail.created_at)}</div>
            </div>
          </div>

          {mail.labels.length > 0 && (
            <div style={{ marginTop: 10, display: "flex", flexWrap: "wrap", gap: 6 }}>
              {mail.labels.map((l) => (
                <span key={l.id} className="label-chip"
                      style={{
                        background: `color-mix(in srgb, ${l.color} 16%, transparent)`,
                        color: l.color,
                      }}>
                  {l.name}
                </span>
              ))}
            </div>
          )}

          <div className="actions">
            <button className="t-btn t-btn-sm t-btn-primary" onClick={() => onReply(mail)} disabled={busy}>
              <I.Reply size={12}/> 답장
            </button>
            <button className="t-btn t-btn-sm" onClick={() => onReplyAll(mail)} disabled={busy}>
              전체답장
            </button>
            {/* 보낸 메일은 '다시 보내기'가 답장·전달보다 자주 필요하다 — 발송 실패나
                누락 때 같은 내용을 첨부까지 그대로 다시 보내기 위한 것. */}
            {mail.folder === "SENT" && (
              <button className="t-btn t-btn-sm" onClick={() => onResend(mail)} disabled={busy}
                title="같은 내용·첨부로 새 메일을 엽니다">
                <I.Send size={12}/> 다시 보내기
              </button>
            )}
            <button className="t-btn t-btn-sm" onClick={() => onForward(mail)} disabled={busy}>
              <I.ArrowR size={12}/> 전달
            </button>
            <button className="t-btn t-btn-sm" onClick={unread} disabled={busy}>안 읽음으로</button>
            <div style={{ position: "relative" }}>
              <button className="t-btn t-btn-sm" onClick={() => setLabelMenuOpen(!labelMenuOpen)} disabled={busy}>
                <I.Filter size={12}/> 라벨
              </button>
              {labelMenuOpen && (
                <div style={{
                  position: "absolute", top: 32, left: 0, zIndex: 90,
                  background: "var(--surface)", border: "1px solid var(--border)",
                  borderRadius: 8, padding: 6, minWidth: 180, boxShadow: "var(--shadow-md)",
                }}>
                  {labels.length === 0 && <div style={{ padding: 8, fontSize: 12, color: "var(--text-3)" }}>라벨이 없습니다</div>}
                  {labels.map((l) => {
                    const on = mail.labels.some((x) => x.id === l.id);
                    return (
                      <button key={l.id}
                              className="t-subnav-item"
                              style={{ width: "100%", justifyContent: "flex-start", gap: 8 }}
                              onClick={() => toggleLabel(l)}>
                        <span style={{
                          width: 14, height: 14, borderRadius: 3, background: l.color, display: "inline-block",
                          border: on ? "2px solid var(--text)" : "1px solid var(--border)",
                        }}/>
                        <span style={{ flex: 1, textAlign: "left" }}>{l.name}</span>
                        {on && <I.Check size={14} style={{ color: "var(--p-600)" }}/>}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            {mail.folder !== "TRASH" && mail.folder !== "SPAM" && (
              <button className="t-btn t-btn-sm" onClick={() => move("INBOX")} disabled={busy || mail.folder === "INBOX"}>받은편지함으로</button>
            )}
            {mail.folder === "SPAM"
              ? <button className="t-btn t-btn-sm" onClick={() => move("INBOX")} disabled={busy}>스팸 아님</button>
              : <button className="t-btn t-btn-sm" onClick={() => move("SPAM")} disabled={busy}>스팸신고</button>}
            <button className="t-btn t-btn-sm t-btn-danger" onClick={trash} disabled={busy}>
              <I.Trash size={12}/> {mail.folder === "TRASH" ? "영구 삭제" : "삭제"}
            </button>
          </div>
        </div>

        {/* 외부 발송이 실패했는데도 보낸메일함에 그대로 남아 정상처럼 보이던 문제.
            사유를 눈에 띄게 띄워서, 상대가 못 받은 걸 뒤늦게 알게 되는 일을 막는다. */}
        {mail.delivery_error && (
          <div style={{
            margin: "10px 14px 0", padding: "10px 12px", borderRadius: 8, fontSize: 12.5,
            border: "1px solid var(--danger, #ef4444)", background: "rgba(239,68,68,.08)",
            color: "var(--danger, #ef4444)", display: "flex", gap: 8, alignItems: "flex-start",
          }}>
            <span style={{ fontSize: 14, lineHeight: 1.2 }}>⚠️</span>
            <span><b>이 메일은 상대에게 전달되지 않았습니다.</b><br/>{mail.delivery_error}</span>
          </div>
        )}

        {/* 성공도 명시한다. 아무 표시가 없으면 잘 나간 건지 아직 처리 중인지 알 수 없어
            보낸 사람이 불안해진다(실제로 도달 여부를 몇 번이나 되물어야 했다). */}
        {mail.folder === "SENT" && !mail.delivery_error && (
          <div className="sent-ok" style={{
            margin: "10px 14px 0", padding: "8px 12px", borderRadius: 8, fontSize: 12,
            border: "1px solid rgba(34,197,94,.35)", background: "rgba(34,197,94,.07)",
            color: "var(--text-2)", display: "flex", gap: 8, alignItems: "flex-start",
          }}>
            <span style={{ fontSize: 13, lineHeight: 1.3 }}>✅</span>
            <span>
              <b style={{ color: "var(--success,#16a34a)" }}>발송 완료</b>
              {" — 메일 서버가 정상적으로 접수해 보냈습니다."}
              <span className="sent-ok-note" style={{ display: "block", color: "var(--text-3)", fontSize: 11.5 }}>
                받는 쪽 서버가 거부하면 반송 알림이 도착하고, 이 자리에 실패로 다시 표시됩니다.
              </span>
            </span>
          </div>
        )}

        {/* 수신 확인 — 네이버 메일처럼 누가 읽었는지 보낸 사람이 바로 본다. */}
        {mail.folder === "SENT" && receipts && receipts.length > 0 && (
          <div style={{
            margin: "8px 14px 0", padding: "8px 12px", borderRadius: 8, fontSize: 12,
            border: "1px solid var(--border)", background: "var(--panel)",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: receiptsOpen ? 4 : 0 }}>
              <button onClick={() => setReceiptsOpen((v) => !v)} aria-expanded={receiptsOpen}
                style={{ display: "flex", alignItems: "center", gap: 8, border: "none", background: "transparent",
                         padding: 0, cursor: "pointer", color: "inherit", font: "inherit" }}>
                <b>수신 확인</b>
                <span style={{ color: "var(--text-3)", fontSize: 11 }}>
                  읽음 {receipts.filter((r) => r.status === "read" || r.status === "opened").length} / {receipts.length}
                  {receiptsOpen ? " ▴" : " ▾"}
                </span>
              </button>
              <button className="t-btn t-btn-sm t-btn-outline" onClick={() => void loadReceipts()} disabled={receiptsBusy}
                style={{ marginLeft: "auto", padding: "1px 8px", fontSize: 11 }}>새로고침</button>
            </div>
            {receiptsOpen && receipts.map((r) => (
              <div key={r.recipient} style={{ display: "flex", gap: 8, alignItems: "center", padding: "2px 0" }}>
                <span style={{
                  width: 8, height: 8, borderRadius: 4, flexShrink: 0,
                  background: r.status === "read" || r.status === "opened" ? "var(--success,#16a34a)"
                    : r.status === "bounced" ? "var(--danger,#ef4444)" : "var(--text-3)",
                }}/>
                <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.recipient}</span>
                <span style={{ color: "var(--text-2)", whiteSpace: "nowrap" }}>{receiptLabel(r)}</span>
              </div>
            ))}
            {receiptsOpen && receipts.some((r) => r.kind === "external") && (
              <div style={{ marginTop: 4, fontSize: 10.5, color: "var(--text-3)", lineHeight: 1.5 }}>
                외부 주소는 상대 메일 앱이 본문 이미지를 불러올 때 열람으로 잡힙니다. 이미지를 차단한 앱에서는 읽어도 '미확인'으로 남을 수 있습니다.
                {receipts.some((r) => r.shared) && " 여러 명에게 한 통으로 보낸 메일은 누가 열었는지 구분되지 않아 한 명이 열면 모두 열람으로 표시됩니다(개별 발송이면 구분됩니다)."}
              </div>
            )}
          </div>
        )}

        {/* 첨부는 본문 **위**에 둔다. 아래에 있으면 긴 메일에서 끝까지 내려야
            보여서, 파일이 왔다는 것조차 놓치기 쉽다(네이버 메일과 같은 배치). */}
        {attCount + oldCount > 0 && (
          <button className="att-fold" onClick={() => setAttOpen((v) => !v)} aria-expanded={attOpen}
            style={{ margin: "6px 14px 0", padding: "4px 10px", borderRadius: 8, cursor: "pointer",
                     border: "1px solid var(--border)", background: "var(--panel)", color: "var(--text-2)",
                     fontSize: 12.5, fontWeight: 600 }}>
            📎 첨부 {attCount}개{oldCount > 0 ? ` · 이전 메일 ${oldCount}개` : ""} {attOpen ? "접기 ▴" : "펼치기 ▾"}
          </button>
        )}
        {(attOpen || attCount === 0) && (<div className="att-area">
        {/* 감춘 서식 이미지가 있으면 알려는 준다 — 숨겼다는 사실까지 감추면 안 된다. */}
        {hiddenCount > 0 && (
          <div style={{ padding: "0 14px", fontSize: 11.5, color: "var(--text-3)" }}>
            본문에 쓰인 서식 이미지 {hiddenCount}개는 목록에서 숨겼습니다.{" "}
            <button onClick={() => setShowHidden((v) => !v)}
              style={{ background: "transparent", border: "none", color: "var(--p-600)",
                       cursor: "pointer", textDecoration: "underline", fontSize: 11.5 }}>
              {showHidden ? "다시 숨기기" : "보기"}
            </button>
          </div>
        )}

        {(showHidden ? (mail.attachments || []) : realAttachments).length > 0 && (
          <div className="attachments">
            {(showHidden ? (mail.attachments || []) : realAttachments).map((a) => {
              // 볼 수 있는 형식이면 눌렀을 때 바로 보여 주고, 아니면 예전처럼 내려받는다.
              // HTML 은 새 탭에 페이지로 연다(보안메일은 화면 안 틀에서는 멈춘다).
              const pk = previewKind(a.file_name, a.mime_type);
              const canPreview = pk !== null;
              return (
                <button key={a.id} className="att-chip"
                  title={pk === "html" ? "새 탭에서 열기" : canPreview ? "미리보기" : "내려받기"}
                  onClick={() => (pk === "html" ? void openHtmlAttachment(a.id)
                    : canPreview ? setPreview(a) : downloadAtt(a.id, a.file_name))}>
                  <I.Paper size={14}/> <span className="att-name">{a.file_name}</span>
                  <span className="size">{formatBytes(a.file_size)}</span>
                  {canPreview && <span style={{ marginLeft: 4, color: "var(--p-600)", fontSize: 11 }}>👁</span>}
                </button>
              );
            })}
          </div>
        )}

        {renderLinkChips(curFileLinks, externalLinks, true)}

        {oldCount > 0 && (
          <div style={{ padding: "8px 14px 0" }}>
            <button onClick={() => setShowOld((v) => !v)} aria-expanded={showOld}
              title="답장 아래에 인용된 예전 메일에 들어 있던 첨부입니다"
              style={{ border: "none", background: "transparent", padding: 0, cursor: "pointer",
                       color: "var(--text-3)", fontSize: 12, textDecoration: "underline" }}>
              이전 메일(인용)에 있던 첨부 {oldCount}개 {showOld ? "숨기기 ▴" : "보기 ▾"}
            </button>
            {showOld && <div style={{ opacity: 0.85 }}>{renderLinkChips(oldFileLinks, oldExternalLinks, false)}</div>}
          </div>
        )}
        </div>)}

        <div className="body">
          {processed ? (
            <>
              {processed.blocked && !showRemote && (
                <div className="mail-remote-banner">
                  <span>🛡️ 보낸사람 추적에 쓰일 수 있는 원격 이미지가 차단되었습니다.</span>
                  <button className="t-btn t-btn-sm" onClick={() => setShowRemotePref(true)}>
                    이미지 표시 (계속)
                  </button>
                </div>
              )}
              {/* 이미지를 보여 주는 중이어도 되돌릴 길은 남겨 둔다 — 추적이 걱정될 때 쓴다. */}
              {processed.hasRemote && showRemote && (
                <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 6 }}>
                  <button
                    onClick={() => setShowRemotePref(false)}
                    title="원격 이미지를 차단하면 열람 추적을 막을 수 있습니다"
                    style={{ background: "transparent", border: "none", color: "var(--text-3)",
                             fontSize: 11, cursor: "pointer", textDecoration: "underline" }}>
                    🛡️ 원격 이미지 차단하기
                  </button>
                </div>
              )}
              {/* 본문 안 메일 주소(mailto) 클릭을 가로채 TimBox 작성창을 연다.
                  기본 동작에 맡기면 외부 메일 프로그램이 열려 흐름이 끊긴다. */}
              <div className="html-body"
                   onClick={(e) => {
                     const a = (e.target as HTMLElement).closest("a");
                     if (!a) return;
                     const href = a.getAttribute("href") || "";
                     const addr = a.getAttribute("data-mail-to")
                       || (href.startsWith("mailto:") ? href.slice(7).split("?")[0] : "");
                     if (addr) { e.preventDefault(); onComposeTo(addr); }
                   }}
                   dangerouslySetInnerHTML={{ __html: processed.html }}/>
            </>
          ) : (
            mail.body_text
              ? <PlainBodyWithLinks text={mail.body_text} onComposeTo={onComposeTo}/>
              : <span style={{ color: "var(--text-3)" }}>(본문 없음)</span>
          )}
        </div>

        {preview && <AttachmentPreview att={preview} onClose={() => setPreview(null)} />}
      {addrMenu && (() => {
        const { name, email } = splitAddr(addrMenu.raw);
        const isSender = splitAddr(mail.from_addr).email === email;
        const W = 226, GAP = 2;
        const vw = typeof window !== "undefined" ? window.innerWidth : 1200;
        const vh = typeof window !== "undefined" ? window.innerHeight : 800;
        const items: { label: string; icon: string; run: () => void }[] = [
          { label: "메일 주소 복사", icon: "\u2398", run: () => void copyText(email).then((ok) =>
              ok ? toast.success("복사했습니다", email) : toast.error("복사하지 못했습니다", email)) },
          { label: "이름과 주소 복사", icon: "\u2398", run: () => void copyText(name && name !== email.split("@")[0] ? `${name} <${email}>` : email).then((ok) =>
              ok ? toast.success("복사했습니다") : toast.error("복사하지 못했습니다")) },
          { label: "이 주소로 메일 쓰기", icon: "\u2709", run: () => onComposeTo(addrMenu.raw) },
        ];
        if (isSender) {
          items.push({ label: "답장", icon: "\u21A9", run: () => onReply(mail) });
          if ((mail.to_addrs?.length ?? 0) + (mail.cc_addrs?.length ?? 0) > 1) {
            items.push({ label: "전체 답장", icon: "\u21A9", run: () => onReplyAll(mail) });
          }
        }
        items.push({ label: "주소록에 저장", icon: "\u2605", run: () => void addToContacts(addrMenu.raw) });
        if (onSearchSender) {
          items.push({ label: "이 사람 메일 모아보기", icon: "\u2315", run: () => onSearchSender(email) });
        }
        const H = items.length * 30 + 12;
        const left = Math.min(addrMenu.x + GAP, Math.max(8, vw - W - 8));
        const top = addrMenu.y + GAP + H > vh ? Math.max(8, addrMenu.y - H) : addrMenu.y + GAP;
        return (
          <div className="addr-menu" style={{ left, top, width: W }}
               onMouseDown={(e) => e.stopPropagation()} onContextMenu={(e) => e.preventDefault()}>
            <div className="am-head" title={addrMenu.raw}>{email}</div>
            {items.map((it) => (
              <button key={it.label} type="button" className="am-item"
                      onMouseDown={(e) => { e.preventDefault(); setAddrMenu(null); it.run(); }}>
                <span className="am-ico">{it.icon}</span>{it.label}
              </button>
            ))}
          </div>
        );
      })()}
    </div>
  );
}

/** 수신 확인 한 줄 문구 */
function receiptLabel(r: MailReceipt): string {
  const when = r.at
    ? new Date(r.at).toLocaleString("ko-KR", { month: "numeric", day: "numeric", hour: "2-digit", minute: "2-digit" })
    : "";
  switch (r.status) {
    case "read": return when ? `읽음 ${when}` : "읽음";
    case "opened": return `열람 ${when}${r.count > 1 ? ` (${r.count}회)` : ""}`;
    case "unread": return "안 읽음";
    case "deleted": return r.count ? "읽고 삭제함" : "안 읽고 삭제함";
    case "bounced": return "반송됨";
    default: return r.kind === "external" ? "미확인" : "확인 불가";
  }
}
